import { createPhantomTypeSchema, DateObj, DateObjT, MaybeT, t, } from '../../../../universal';
import { _, React } from '../../../lib';
import { Checkbox as CheckboxInner } from '../checkbox';
import { DateBox } from '../date';
import { TextareaWithVoice } from '../text-with-voice';
import { Textbox } from '../textbox';
import { FormTypeAddress, FormTypeContact } from './fields-address-contact';
import { FormTypeColourPicker } from './fields-colour';
import { FormTypeDropdown, FormTypeDropdownMulti, FormTypeDropdownMultiStringKey, FormTypeDropdownStringKey, } from './fields-dropdown';
import { FormTypeFileMultiple, FormTypeFileSingle, FormTypeFileTempSingle, } from './fields-file';
import { FormTypeNumeric } from './fields-numeric';
import { readDynamic } from './types';
export const Text = (settings) => ({
    ...settings,
    valueDefaults: {
        def: () => '',
        fixerImmediate: x => (settings.autoCapitalize ?? true) && x
            ? x.charAt(0).toUpperCase() + x.slice(1)
            : x,
        fixer: x => ((settings.autoTrim ?? true) ? x?.trim() : x) ?? '',
        validators: [
            {
                req: x => {
                    const len = settings.minLength;
                    if ((settings.nullable ?? false) && !x) {
                        return true;
                    }
                    return Boolean(len == null || (x?.length ?? 0) >= len);
                },
                msg: `must be at least ${settings.minLength} chars`,
            },
            {
                req: x => {
                    const len = settings.maxLength;
                    return Boolean(len == null || (x?.length ?? 0) <= len);
                },
                msg: `cannot exceed ${settings.maxLength} chars`,
            },
        ],
    },
    height: 36,
    typeMap: {
        schemaPublic: createPhantomTypeSchema(t.String()),
        schemaRaw: createPhantomTypeSchema(t.String()),
        toPublic: (x) => x,
        toInternal: (x) => x,
    },
    llmInfo: formState => ({
        stringifiedType: 'text',
        description: readDynamic(settings.doc, formState) ?? readDynamic(settings.lbl, formState),
        reversal: x => String(x ?? ''),
    }),
    jsx: props => (React.createElement(Textbox, { value: props.value, onUpdate: props.onUpdate, autoCapitalize: settings.autoCapitalize === false ? 'none' : undefined, className: props.className ?? undefined, title: props.title ?? undefined, placeholder: settings.placeholder, readOnly: props.readOnly, disabled: props.disabled, onFocus: props.onFocus, onBlur: props.onBlur, "data-1p-ignore": true })),
});
export const TextMultiline = (settings) => ({
    ...settings,
    valueDefaults: {
        def: () => '',
        fixerImmediate: x => {
            let s = x;
            if (settings.autoCapitalize ?? true) {
                s = s.charAt(0).toUpperCase() + s.slice(1);
            }
            if (settings.disableNewlines ?? false) {
                s = s.replace(/\n/g, ' ');
            }
            return s;
        },
        fixer: x => ((settings.autoTrim ?? true) ? x?.trim() : x) ?? '',
        validators: [
            {
                req: x => {
                    const len = settings.minLength;
                    return Boolean(len == null || (x?.length ?? 0) >= len);
                },
                msg: `must be at least ${settings.minLength} chars`,
            },
            {
                req: x => {
                    const len = settings.maxLength;
                    return Boolean(len == null || (x?.length ?? 0) <= len);
                },
                msg: `cannot exceed ${settings.maxLength} chars`,
            },
        ],
    },
    emptyLabel: settings.fullWidth ?? false,
    removeLabel: settings.fullWidth ?? false,
    height: (_.max([settings.rows, 1]) ?? 1) * 20 + 38,
    typeMap: {
        schemaPublic: createPhantomTypeSchema(t.String()),
        schemaRaw: createPhantomTypeSchema(t.String()),
        toPublic: x => x,
        toInternal: x => x,
    },
    llmInfo: formState => ({
        stringifiedType: 'text',
        description: readDynamic(settings.doc, formState) ?? readDynamic(settings.lbl, formState),
        reversal: x => String(x ?? ''),
    }),
    jsx: (props, formState) => (React.createElement(TextareaWithVoice, { value: props.value, onUpdate: props.onUpdate, className: props.className ?? undefined, title: props.title ?? undefined, rows: settings.rows < 1 ? 1 : settings.rows, placeholder: settings.placeholder, readOnly: props.readOnly, disabled: props.disabled, onFocus: props.onFocus, onBlur: props.onBlur, "data-1p-ignore": true, prompt: settings.voicePrompt ?? readDynamic(settings.doc, formState) ?? '', maxLength: settings.maxLength, disableVoiceInput: Boolean(settings.disableVoiceInput || props.readOnly || props.disabled) })),
});
export const Date = (settings) => ({
    ...settings,
    valueDefaults: {
        def: () => null,
        validators: [
            settings.minDate && {
                req: x => {
                    const d = DateObj.parse(x);
                    const minDate = settings.minDate;
                    if (!minDate) {
                        return true;
                    }
                    return Boolean(d && d >= minDate);
                },
                msg: `must be after ${settings.minDate?.fmtDMY()}`,
            },
            settings.maxDate && {
                req: x => {
                    const d = DateObj.parse(x);
                    const maxDate = settings.maxDate;
                    if (!maxDate) {
                        return true;
                    }
                    return Boolean(d && d <= maxDate);
                },
                msg: `must be before ${settings.maxDate.fmtDMY()}`,
            },
        ],
    },
    height: 36,
    typeMap: {
        schemaPublic: createPhantomTypeSchema(MaybeT(DateObjT())),
        schemaRaw: createPhantomTypeSchema(MaybeT(DateObjT())),
        toPublic: x => x,
        toInternal: x => x,
    },
    llmInfo: formState => ({
        stringifiedType: 'date',
        description: `Return in YYYY-MM-DD format. ${readDynamic(settings.doc, formState) ?? readDynamic(settings.lbl, formState)}`,
        reversal: x => (Boolean(x) ? DateObj.parse(String(x)) : null),
    }),
    jsx: props => (React.createElement(DateBox, { value: props.value, onUpdate: props.onUpdate, className: props.className ?? undefined, title: props.title ?? undefined, placeholder: settings.placeholder, defaultZoom: settings.defaultZoom, fmt: settings.format, readOnly: props.readOnly, disabled: props.disabled, onFocus: props.onFocus, onBlur: props.onBlur, "data-1p-ignore": true })),
});
export const Checkbox = (settings) => ({
    ...settings,
    valueDefaults: {
        def: () => false,
        validators: [],
    },
    height: 36,
    emptyLabel: true,
    removeLabel: settings.fullWidth ?? false,
    typeMap: {
        schemaPublic: createPhantomTypeSchema(t.Boolean()),
        schemaRaw: createPhantomTypeSchema(t.Boolean()),
        toPublic: x => x,
        toInternal: x => x,
    },
    llmInfo: formState => ({
        stringifiedType: 'boolean',
        description: readDynamic(settings.doc, formState) ?? readDynamic(settings.lbl, formState),
        reversal: x => Boolean(x),
    }),
    jsx: props => (React.createElement(CheckboxInner, { lbl: props.lbl, value: props.value, onUpdate: props.onUpdate, className: props.className ?? undefined, title: props.title ?? undefined, readOnly: props.readOnly, disabled: props.disabled, onFocus: props.onFocus, onBlur: props.onBlur })),
});
export const FormType = {
    Text: Text,
    Textarea: TextMultiline,
    Numeric: FormTypeNumeric,
    Date: Date,
    Checkbox: Checkbox,
    Dropdown: FormTypeDropdown,
    DropdownStringKey: FormTypeDropdownStringKey,
    DropdownMulti: FormTypeDropdownMulti,
    DropdownMultiStringKey: FormTypeDropdownMultiStringKey,
    Address: FormTypeAddress,
    Contact: FormTypeContact,
    FileUploadSingle: FormTypeFileSingle,
    FileUploadMultiple: FormTypeFileMultiple,
    FileUploadTempSingle: FormTypeFileTempSingle,
    Colour: FormTypeColourPicker,
};
//# sourceMappingURL=fields.js.map